import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CheckBox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';

import { Env } from '@functions/index';

import { Token } from '@hooks/index';

import { AdminLayout } from '@layout/Admin/AdminLayout';
import DataGrid from '@common/DataGrid/DataGrid';
import Time from '@functions/time';

import styles from './style.scss';

export type Unavailability = {
  _id: string;
  sinceDate: string;
  untilDate: string;
  showOnHomePage: boolean;
  text?: string;
  createdAt: string;
};

const AdminUnavailability = (): JSX.Element => {
  const [data, setData] = useState<Unavailability[] | null>(null);
  const [values, setValues] = useState<Partial<Unavailability>>({});
  const [loading, setLoading] = useState(true);

  const history = useHistory();

  const loggedIn = Token.useTokenValidation();

  useEffect(() => {
    loadData();
  }, [loggedIn]);

  const loadData = () => {
    fetch(`${Env.apiUrl()}/rovi-sport/unavailability/search`, {
      ...Env.apiHeaders(),
      method: 'POST',
      body: JSON.stringify({
        sortDir: 'DESC',
      }),
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((error) => {
            throw error;
          });
        }
        return response.json();
      })
      .then((resp) => {
        setData(resp);
        setLoading(false);
      })
      .catch((error) => {
        if (Env.isLocalhost()) {
          // setData();
        }

        if (error.statusCode === 401) {
          localStorage.removeItem('user-session-rs');
          window.dispatchEvent(new Event('storage'));
        }
      });
  };

  const onCreateUnavailability = () => {
    fetch(`${Env.apiUrl()}/rovi-sport/unavailability`, {
      method: 'POST',
      ...Env.apiHeaders(),
      body: JSON.stringify({
        sinceDate: new Date(values.sinceDate!).toISOString(),
        untilDate: new Date(values.untilDate!).toISOString(),
        showOnHomePage: values.showOnHomePage,
        text: values.text,
      }),
    }).then(() => {
      loadData();
      setValues({});
    });
  };

  const onDeleteUnavailability = (id: string) => {
    fetch(`${Env.apiUrl()}/rovi-sport/unavailability/${id}`, {
      ...Env.apiHeaders(),
      method: 'DELETE',
    }).then(() => {
      loadData();
    });
  };

  const config = [
    {
      id: 'sinceDate',
      label: 'Od',
      render: (row: Unavailability) => Time.formatDate(row.sinceDate),
    },
    {
      id: 'untilDate',
      label: 'Do',
      render: (row: Unavailability) => Time.formatDate(row.untilDate),
    },
    {
      id: 'showOnHomePage',
      label: 'Info na hlavnej stránke',
      render: (row: Unavailability) => (row.showOnHomePage ? 'Zobrazené' : 'Skryté'),
    },
    { id: 'text', label: 'Dôvod nedostupnosti', render: (row: Unavailability) => (row.showOnHomePage && row.text) || '' },
    {
      id: 'actions',
      label: 'Akcie',
      render: (row: Unavailability) => (
        <Button
          size="small"
          color="error"
          variant="outlined"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onDeleteUnavailability(row._id);
          }}
        >
          Odstrániť
        </Button>
      ),
    },
  ];

  return (
    <AdminLayout
      toolbar={
        <div className={styles.subHeader}>
          <div className={styles.sectionTitle}>Nedostupnosť</div>
        </div>
      }
    >
      <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
        <Box display="flex" flexDirection="column">
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            p={2}
            pb={values.sinceDate && values.untilDate ? 2 : 0}
            sx={{ backgroundColor: '#eee', borderRadius: 2, transition: '0.3s ease all' }}
          >
            <Box display="flex" gap={2}>
              <TextField
                type="date"
                InputLabelProps={{ shrink: true }}
                label="Pridať nedostupnosť od"
                value={values.sinceDate}
                onChange={(e) => {
                  setValues({ ...values, sinceDate: e.target.value, untilDate: values.untilDate ? values.untilDate : e.target.value });
                }}
              />
              <TextField
                type="date"
                label="Pridať nedostupnosť do"
                InputLabelProps={{ shrink: true }}
                value={values.untilDate}
                onChange={(e) => {
                  setValues({ ...values, untilDate: e.target.value });
                }}
              />
            </Box>
            <Collapse in={!!(values.sinceDate && values.untilDate)}>
              <Box display="flex" flexDirection="column" gap={2} sx={{ whiteSpace: 'nowrap' }}>
                <FormControl sx={{ border: `1px solid #bbb`, borderRadius: 1, padding: `5px 10px` }}>
                  <FormControlLabel
                    control={
                      <CheckBox
                        checked={values.showOnHomePage}
                        onChange={(e) => {
                          setValues({ ...values, showOnHomePage: e.target.checked });
                        }}
                      />
                    }
                    label="Zobraziť text nedostupnosti"
                  />
                </FormControl>
                <Collapse in={!!values.showOnHomePage}>
                  <TextField
                    type="text"
                    label="Dôvod nedostupnosti - nepovinné"
                    value={values.text}
                    fullWidth
                    onChange={(e) => {
                      setValues({ ...values, text: e.target.value });
                    }}
                  />
                </Collapse>
                <Button color="primary" variant="contained" onClick={() => onCreateUnavailability()}>
                  Pridať nedostupnosť
                </Button>
              </Box>
            </Collapse>
          </Box>
        </Box>
      </Box>
      <div className={styles.subHeader}>
        <div className={styles.content}>
          <div className={styles.dataGridWrap}>
            {data ? (
              <DataGrid<Unavailability> data={data} config={config} />
            ) : (
              <Box sx={{ minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={30} color="secondary" />
              </Box>
            )}
          </div>
          <div className={styles.foot}>
            <Button onClick={() => history.push('/')}>Zobraziť web</Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminUnavailability;
